import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useAuth } from "../hooks/useAuth";
import "./CSS/auth.css";
import cael from "./CSS/cael.png";

const VerifyEmail = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyEmail, resendVerification } = useAuth();
  
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [message, setMessage] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    const code = searchParams.get("code");
    const email = searchParams.get("email");

    if (!code || !email) {
      setStatus("error");
      setError(t('verifyEmail.errors.invalidLink', { defaultValue: 'Invalid verification link. Please check your email and try again.' }));
      return;
    }

    // Automatically verify email when component mounts
    const verify = async () => {
      try {
        setStatus("verifying");
        setError(null);
        
        const result = await verifyEmail({ code, email });
        
        if (result.status === "success") {
          setStatus("success");
          setMessage(t('verifyEmail.success.message', { defaultValue: 'Your email has been verified successfully! You can now log in.' }));
          
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate("/login", { replace: true });
          }, 3000);
        } else {
          setStatus("error");
          setError(result.message || t('verifyEmail.errors.verificationFailed'));
        }
      } catch (err) {
        setStatus("error");
        setError(err.message || t('verifyEmail.errors.verificationFailed'));
      }
    };

    verify();
  }, [searchParams, verifyEmail, navigate, t]);

  const handleResend = async () => {
    const email = searchParams.get("email");
    if (!email) {
      setError(t('verifyEmail.errors.emailNotFound', { defaultValue: 'Email not found. Please request a new verification email.' }));
      return;
    }

    try {
      setError(null);
      const result = await resendVerification({ email });
      if (result.status === "success") {
        alert(t('verifyEmail.resendSuccess', { defaultValue: 'Verification email sent! Please check your inbox.' }));
      } else {
        setError(result.message || t('verifyEmail.errors.resendFailed', { defaultValue: 'Failed to resend verification email.' }));
      }
    } catch (err) {
      setError(err.message || t('verifyEmail.errors.resendFailed', { defaultValue: 'Failed to resend verification email.' }));
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-bg"></div>

      <div className="auth-card">
        <Link to="/" className="auth-logo-link">
          <img className="auth-logo" src={cael} alt="St Cael Vog" />
        </Link>

        {status === "verifying" && (
          <>
            <h2>{t('verifyEmail.title')}</h2>
            <p className="auth-subtitle">{t('verifyEmail.subtitle', { defaultValue: 'Please wait while we verify your email address...' })}</p>
            <div className="auth-loading">
              <div className="auth-spinner"></div>
            </div>
          </>
        )}

        {status === "success" && (
          <>
            <div className="auth-success">
              <div className="auth-success-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <h2>{t('verifyEmail.success.title', { defaultValue: 'Email Verified!' })}</h2>
              <p className="auth-success-text">{message}</p>
              <p className="auth-hint">{t('verifyEmail.success.redirecting', { defaultValue: 'Redirecting to login page...' })}</p>
            </div>
            <Link to="/login" className="auth-back-link">
              {t('verifyEmail.success.goToSignIn', { defaultValue: 'Go to Sign In' })}
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <div className="auth-error-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <h2>{t('verifyEmail.errors.title', { defaultValue: 'Verification Failed' })}</h2>
            {error && <div className="auth-error">{error}</div>}
            <p className="auth-hint">
              {t('verifyEmail.errors.expiredLink', { defaultValue: 'The verification link may have expired or is invalid.' })}
            </p>
            <div className="auth-actions">
              <button
                type="button"
                onClick={handleResend}
                className="auth-submit-btn"
              >
                {t('verifyEmail.resend', { defaultValue: 'Request New Verification Email' })}
              </button>
              <Link to="/login" className="auth-back-link">
                {t('verifyEmail.success.goToSignIn', { defaultValue: 'Go to Sign In' })}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;

